/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { ApiResponse } from "../../interfaces/Api";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import { ILeaveBalance } from "../../interfaces/LeaveBalanceInterface";
import {
  getLeaveBalances,
  postLeaveBalance,
  patchLeaveBalance,
  deleteLeaveBalance
} from "../../api/leaveBalance";

export const leaveBalancesQueryKey = "leaveBalances";

// use leave balances
export const useLeaveBalances = ({
  serviceProviderIds = [],
  employeeIds = [],
  enabled = true
}: {
  serviceProviderIds?: Array<number>;
  employeeIds?: Array<number>;
  enabled?: boolean;
} = {}): UseQueryResult<Array<ILeaveBalance>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [leaveBalancesQueryKey, resourceCentre?.id, serviceProviderIds, employeeIds],
    queryFn: async () => {
      const response = await getLeaveBalances({
        resourceCentreId: Number(resourceCentre.id),
        serviceProviderIds,
        employeeIds
      });

      return response.data || [];
    },
    enabled: !!resourceCentre?.id && enabled
  });
};

// use post leave balance
export const usePostLeaveBalance = (): UseMutationResult<
  ApiResponse<ILeaveBalance>,
  Error,
  Partial<ILeaveBalance>,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ILeaveBalance>) => postLeaveBalance(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [leaveBalancesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Leave balance created successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create leave balance",
          variant: "error"
        })
      );
    }
  });
};

// use patch leave balance
export const usePatchLeaveBalance = (): UseMutationResult<
  ApiResponse<ILeaveBalance>,
  Error,
  { id: number | string; data: Partial<ILeaveBalance> },
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => patchLeaveBalance({ id, data }),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [leaveBalancesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Leave balance updated successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to update leave balance",
          variant: "error"
        })
      );
    }
  });
};

// use delete leave balance
export const useDeleteLeaveBalance = (): UseMutationResult<
  ApiResponse<null>,
  Error,
  number | string,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteLeaveBalance(id),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: [leaveBalancesQueryKey] });
        dispatch(
          notificationAdd({
            id: Date.now(),
            autoTimeout: true,
            message: "Leave balance deleted successfully",
            variant: "success"
          })
        );
      }
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to delete leave balance",
          variant: "error"
        })
      );
    }
  });
};
